import { useEffect, useState } from 'react';
import Button from '@atoms/new/button';
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@atoms/new/modal';
import { Radio } from '@atoms/new/RadioGroup';
import { useReaderPickerStore } from '@lib/realtimeSync/readerPicker';
import type { ReaderOption } from '@lib/realtimeSync/readerTarget';
import type { TabThrottle } from '@lib/realtimeSync/client';

// "StreamAlive is open in N browsers. Which one should read chat?"
// (refs/reader-ownership.md, "The picker"). Mounted once in pages/_app.js; shows
// whatever request a connect flow made through `requestReaderPick`.

const THROTTLE_NOTE: Partial<Record<TabThrottle, string>> = {
  intensive: 'tab throttled in the background',
  frozen: 'tab frozen in the background',
  discarded: 'tab unloaded by Chrome',
};

const platformName = (option: ReaderOption) => {
  const platform = option.page?.platform;
  if (!platform) return null;
  return platform.charAt(0).toUpperCase() + platform.slice(1);
};

const rowTitle = (option: ReaderOption) => {
  if (option.isLocal) return 'This browser';
  return option.reader.label || option.reader.userEmail || 'Another browser';
};

const rowDetail = (option: ReaderOption) => {
  const parts: string[] = [];
  if (option.isLocal && option.reader.userEmail) parts.push(option.reader.userEmail);
  const platform = platformName(option);
  parts.push(option.page ? `${platform || 'Live'} tab open` : 'live page not open');
  const note = option.throttle && THROTTLE_NOTE[option.throttle];
  if (note) parts.push(note);
  if (option.holdsSession) parts.push('reading now');
  if (!option.canChoose) parts.push('update the extension to choose it');
  return parts.join(' · ');
};

const ReaderPickerModal = () => {
  const request = useReaderPickerStore((s) => s.request);
  const answer = useReaderPickerStore((s) => s.answer);
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    setSelected(request?.preselect ?? null);
  }, [request]);

  if (!request) return null;

  return (
    <Modal size="md" isOpen={true} onClose={() => answer(null)}>
      <ModalContent>
        <ModalHeader className="text-base">
          StreamAlive is open in {request.options.length} browsers. Which one should read chat?
        </ModalHeader>
        <ModalBody>
          <div className="flex flex-col gap-2">
            {request.options.map((option) => (
              <Radio
                key={option.reader.readerId}
                value={option.reader.readerId}
                selectedValue={selected}
                onSelect={() => setSelected(option.reader.readerId)}
                isDisabled={!option.canChoose}
                vertical={true}
                showIndicator={true}
              >
                <div className="flex flex-col pr-6">
                  <span className="text-sm font-semibold text-default-700">{rowTitle(option)}</span>
                  <span className="text-xs text-default-500">{rowDetail(option)}</span>
                </div>
              </Radio>
            ))}
          </div>
          <p className="text-xs text-default-500">
            Only one browser reads a session&apos;s chat at a time.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button kind="secondary" onPress={() => answer(null)}>
            Cancel
          </Button>
          <Button kind="primary" isDisabled={!selected} onPress={() => answer(selected)}>
            Read chat here
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ReaderPickerModal;
